import { Trans, useTranslation } from "react-i18next";
import { useCallback, useDeferredValue, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import type { FacilityNode, PatchRoleBody, RoleCard, RoleReadOnlyReason } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { NO_LEVEL_ICON, PermissionLevelBadge } from "@/components/permission-level";
import { HpfpLadder } from "./HpfpLadder.js";
import { FilterField } from "./FilterField.js";
import { GrantTree } from "./GrantTree.js";
import { Disclosure } from "@/components/ui/disclosure";
import { rootFolderPath } from "./role-groups.js";
import { RoleWriteError, useGrantWrites, usePatchRole, useRoleGrants, useRoleMembers, useRoles } from "./useRoles.js";
import type { AuthSession } from "../../auth/keycloak.js";

/** Message KEYS per read-only reason — the map is module-level and cannot call a hook; the record's keys are the
 *  contract's own `RoleReadOnlyReason` tokens and must stay as they are. */
const READ_ONLY_COPY_KEY = {
  ADMIN_ROLE: "roles.adminRolesCanOnlyBeChanged",
  OWN_ROLE: "roles.youCantChangeARoleYou",
  SUPER_ADMIN: "roles.theSuperadminRoleHasNoConfigurable",
  HIDDEN: "roles.thisRoleIsHiddenFromAccess",
} as const satisfies Record<RoleReadOnlyReason, string>;

/**
 * The selected role (ADR-030 §3.4, read-only in Phase A): the full name, the read-only note the SERVER decided, the
 * level line with the legacy's collapsed ladder behind a caret, the root folder (supers only — a navigation start,
 * not an access boundary), and the facility grants tree.
 */
export function RoleEditor({ role, callerIsSuper, session, flat, bypass }: { role: RoleCard; callerIsSuper: boolean; session: AuthSession; flat: readonly FacilityNode[]; bypass: boolean }) {
  const { t } = useTranslation();
  const [ladderOpen, setLadderOpen] = useState(false);
  const grants = useRoleGrants(session, role.id);
  const roles = useRoles(session);
  const patch = usePatchRole(session);
  const [notice, setNotice] = useState<{ kind: "stale" | "error"; text: string } | null>(null);
  // Phase C — the grant writes: a live region says how many facilities a write reached (an apply-to-children write is
  // partial by design — only the subtree the caller administers), a failure goes inline.
  const grantWrites = useGrantWrites(session);
  const [announce, setAnnounce] = useState<string | null>(null);
  const [pendingFacilityId, setPendingFacilityId] = useState<number | null>(null);
  const [facilityQuery, setFacilityQuery] = useState("");
  // The field paints every keystroke at once; the (heavier) tree re-filters on the deferred value (owner: the filter
  // lagged per keystroke). The rows themselves are memoised, so the callbacks below must be STABLE.
  const deferredFacilityQuery = useDeferredValue(facilityQuery);
  const afterGrant = useCallback(
    (verb: string) => ({
      onSuccess: (r: { affected: number; facilityIds: number[] }) => setAnnounce(t("roles.grantsUpdated", { verb, count: r.affected })),
      onError: (err: RoleWriteError) => {
        const text =
          err.code === "GRANT_ABOVE_HPFP" ? t("roles.thatLevelIsAboveTheRoles") :
          err.code === "GRANT_SUPER_ONLY" ? t("roles.onlyASuperadminCanGrantAdmin") :
          err.code === "FACILITY_FORBIDDEN" ? t("roles.youDontAdministerThatFacility") :
          err.code === "ROLE_HPFP_UNSET" ? t("roles.setTheRolesHighestLevelFirst") : t("roles.couldntSaveThePermissionPleaseTry");
        setNotice({ kind: "error", text });
      },
      onSettled: () => setPendingFacilityId(null),
    }),
    // `t` IS a dependency: react-i18next hands back `i18n.getFixedT(currentLanguage)`, so a callback that captures
    // it keeps announcing in whatever language was active when the callback was memoised (diff panel).
    [t],
  );
  const putMutate = grantWrites.put.mutate;
  const revokeMutate = grantWrites.revoke.mutate;
  const grant = useCallback(
    (facilityId: number, level: Parameters<typeof putMutate>[0]["body"]["level"], applyToChildren: boolean) => {
      setNotice(null);
      setAnnounce(null);
      setPendingFacilityId(facilityId);
      putMutate({ roleId: role.id, facilityId, body: { level, applyToChildren } }, afterGrant(t("roles.permissionSaved")));
    },
    [afterGrant, putMutate, role.id],
  );
  const revoke = useCallback(
    (facilityId: number, applyToChildren: boolean) => {
      setNotice(null);
      setAnnounce(null);
      setPendingFacilityId(facilityId);
      revokeMutate({ roleId: role.id, facilityId, applyToChildren }, afterGrant(t("roles.permissionRemoved")));
    },
    [afterGrant, revokeMutate, role.id],
  );
  const root = useMemo(() => rootFolderPath(flat, role.userRootFacilityId), [flat, role.userRootFacilityId]);
  const members = useRoleMembers(session, role.id);
  // The legacy dashboard's two role hints ("no permission assigned" / "no facilities assigned") are this view's empty
  // states (Phase D): a level alone grants nothing. A missing level is NOT "no access", though: the engine reads a
  // null cap as UNCAPPED (`COALESCE(hpfp, 'access_manage')`), so a legacy/ETL "level 0" role that still carries
  // grants is live at their raw level — the hint must say so, never "cannot see" (panel).
  const noGrants = grants.data !== undefined && grants.data.grants.length === 0 && grants.data.facilityIdsWithChildGrants.length === 0;
  const hint =
    grants.data === undefined
      ? null
      : role.hpfp === null
        ? noGrants
          ? t("roles.noLevelNoGrants", { role: role.shortName })
          : t("roles.noLevelWithGrants", { role: role.shortName, count: grants.data.grants.length })
        : noGrants
          ? t(role.editable ? "roles.noGrantsWithLevel" : "roles.noGrantsWithLevelReadOnly", { role: role.shortName })
          : null;
  // Every write carries the card's token; a 409 STALE means the role moved under us → the list refetches (the card
  // re-renders with the fresh token) and the note says so — never a silent overwrite, never an error dialog.
  const patchMutate = patch.mutate;
  const refetchRoles = roles.refetch;
  const write = useCallback(
    (body: Omit<PatchRoleBody, "expectedUpdatedAt">) => {
      setNotice(null);
      patchMutate(
        { id: role.id, body: { ...body, expectedUpdatedAt: role.updatedAt } },
        {
          onError: (err) => {
            if (err instanceof RoleWriteError && err.code === "ROLE_STALE") {
              void refetchRoles();
              setNotice({ kind: "stale", text: t("roles.thisRoleChangedSinceYouOpened") });
            } else if (err instanceof RoleWriteError && err.code === "HPFP_SUPER_ONLY") {
              setNotice({ kind: "error", text: t("roles.onlyASuperadminCanGrantThe") });
            } else {
              setNotice({ kind: "error", text: t("roles.couldntSaveTheChangePleaseTry") });
            }
          },
        },
      );
    },
    [patchMutate, refetchRoles, role.id, role.updatedAt, t],
  );
  const setRoot = useCallback((facilityId: number) => write({ userRootFacilityId: facilityId }), [write]);
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 id="role-editor-title" className="text-lg font-semibold break-all">
          {role.name}
        </h2>
        {role.readOnlyReason ? <p className="text-muted-foreground mt-1 text-sm">{t(READ_ONLY_COPY_KEY[role.readOnlyReason])}</p> : null}
        {notice ? (
          <p role={notice.kind === "error" ? "alert" : "status"} className={notice.kind === "error" ? "text-destructive mt-1 text-sm" : "text-muted-foreground mt-1 text-sm"}>
            {notice.text}
          </p>
        ) : null}
        {callerIsSuper && role.editable ? (
          <div className="mt-2">
            <Switch isSelected={role.hidden} isDisabled={patch.isPending} onChange={(v) => write({ hidden: v })} aria-label={t("roles.hiddenFromAccessManagement")}>
              {t("roles.hiddenFromAccessManagement")}
            </Switch>
          </div>
        ) : null}
      </div>

      <section aria-labelledby="role-level-heading" className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 id="role-level-heading" className="text-sm font-medium">
            {t("roles.highestLevelOfThisRole")}
          </h3>
          {role.hpfp ? (
            <PermissionLevelBadge level={role.hpfp} />
          ) : (
            <span className="text-attention inline-flex items-center gap-1 text-sm font-medium">
              <NO_LEVEL_ICON aria-hidden="true" className="size-4" />
              {t("roles.noPermissionLevelSet")}
            </span>
          )}
          <Button variant="ghost" size="sm" aria-expanded={ladderOpen} aria-controls="hpfp-ladder" onPress={() => setLadderOpen((v) => !v)} className="gap-1">
            {ladderOpen ? <ChevronUp aria-hidden="true" className="size-4" /> : <ChevronDown aria-hidden="true" className="size-4" />}
            {ladderOpen ? t("roles.hideLevels") : t("roles.showLevels")}
          </Button>
        </div>
        {ladderOpen ? (
          <div id="hpfp-ladder">
            <HpfpLadder hpfp={role.hpfp} callerIsSuper={callerIsSuper} canEdit={role.editable} isPending={patch.isPending} onChange={(level) => write({ hpfp: level })} />
          </div>
        ) : null}
      </section>

      {callerIsSuper ? (
        <p className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
          <span>
            {root.kind === "none"
              ? t("roles.noBaseFolderSet")
              : root.kind === "missing"
                ? t("roles.baseFolderMissing", { id: root.id })
                : t("roles.baseFolderPath", { path: root.names.join(" › ") })}
          </span>
          {root.kind !== "none" && role.editable ? (
            <Button variant="ghost" size="sm" isDisabled={patch.isPending} onPress={() => write({ userRootFacilityId: null })}>
              {t("roles.clearBaseFolder")}
            </Button>
          ) : null}
        </p>
      ) : null}

      <section aria-labelledby="role-grants-heading" className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="role-grants-heading" className="text-sm font-medium">
            {t("roles.facilityPermissions")}
          </h3>
          <FilterField label={t("roles.filterFacilities")} placeholder={t("roles.filterFacilitiesByName")} value={facilityQuery} onChange={setFacilityQuery} className="w-64" />
        </div>
        <p aria-live="polite" className={announce ? "text-muted-foreground text-xs" : "sr-only"}>
          {announce ?? ""}
        </p>
        {hint ? (
          <p role="note" className="text-attention bg-attention/10 max-w-prose rounded-md px-3 py-2 text-sm">
            {hint}
          </p>
        ) : null}
        {role.editable && role.hpfp !== null ? (
          <p className="text-muted-foreground text-xs">
            <Trans i18nKey="roles.grantHelp" components={{ toggle: <>{t("roles.subFacilitiesToo")}</> }} />
          </p>
        ) : null}
        {grants.isPending ? (
          <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("roles.loadingEllipsis")}
          </p>
        ) : grants.isError ? (
          <div className="flex flex-col items-start gap-2">
            <p role="alert" className="text-destructive text-sm">
              {t("roles.couldntLoadThisRolesPermissions")}
            </p>
            <Button variant="outline" size="sm" onPress={() => void grants.refetch()}>
              {t("roles.tryAgain")}
            </Button>
          </div>
        ) : (
          <GrantTree
            flat={flat}
            grants={grants.data.grants}
            childGrantIds={grants.data.facilityIdsWithChildGrants}
            bypass={bypass}
            rootFacilityId={callerIsSuper ? role.userRootFacilityId : null}
            onSetRoot={callerIsSuper && role.editable ? setRoot : undefined}
            hpfp={role.hpfp}
            canEdit={role.editable}
            pendingFacilityId={pendingFacilityId}
            onGrant={grant}
            onRevoke={revoke}
            query={deferredFacilityQuery}
          />
        )}
      </section>

      <section aria-labelledby="role-members-heading" className="flex flex-col gap-2">
        {/* Read-only (ADR-030 §5 #2): membership is Keycloak's — this is the roster from `user_role`, collapsed by default;
            no "managed in Keycloak" line (owner: the roster speaks for itself). */}
        <Disclosure
          title={
            <span id="role-members-heading" className="text-sm font-medium">
              {members.data ? t("roles.membersCount", { count: members.data.members.length }) : t("roles.members")}
            </span>
          }
        >
          <div className="px-3 pb-2">
          {members.isPending ? (
            <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("roles.loadingEllipsis")}
            </p>
          ) : members.isError ? (
            <div className="flex flex-col items-start gap-2">
              <p role="alert" className="text-destructive text-sm">
                {t("roles.couldntLoadThisRolesMembers")}
              </p>
              <Button variant="outline" size="sm" onPress={() => void members.refetch()}>
                {t("roles.tryAgain")}
              </Button>
            </div>
          ) : members.data.members.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("roles.noUsersHoldThisRole")}</p>
          ) : (
            <ul aria-label={t("roles.members")} className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
              {members.data.members.map((m) => (
                <li key={m.userId}>{m.name}</li>
              ))}
            </ul>
          )}
          </div>
        </Disclosure>
      </section>
    </div>
  );
}
